import React, { useState } from 'react';
import PropTypes from 'prop-types';
import zxcvbn from 'zxcvbn';
import copyToClipboard from 'copy-to-clipboard';
import generator from 'generate-password';
import validator from 'validator';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';

import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import CheckIcon from '@material-ui/icons/CheckCircle';
import { Typography } from '@material-ui/core';

const UserForm = (props) => {
  const [email, setEmail] = useState(props.email || '');
  const [emailError, setEmailError] = useState(false);
  const [role, setRole] = useState(props.role || 'viewer');
  const [password, setPassword] = useState({ value: '', score: 0, feedback: [], clipBoard: false });
  const [showPassword, setShowPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(props.type === 'edit');

  const actionByType = () => {
    if (props.type === 'create') {
      const disabled = !(email && password.score > 3 && (!emailError));
      return (
        <Button variant='contained' color='primary' autoFocus disabled={disabled}
          onClick={() => props.handleOk({ email, role, password: password.value })}
        >Create
        </Button>
      );
    } else {
      const disabled = !((password.value && password.score > 3) || (role !== props.role)) || emailError;
      const changeVal = { email, role };
      if (!showChangePassword) {
        changeVal.password = password.value;
      }
      return (
        <Button variant='contained' color='primary' autoFocus disabled={disabled}
          onClick={() => props.handleOk(changeVal)}
        >Save changes</Button>
      );
    }
  };
  const titleByType = () => {
    if (props.type === 'create') {
      return 'Create New User';
    } else {
      return 'Edit User';
    }
  };

  const setPasswordValue = (value='', clipBoard=false) => {
    const strength = zxcvbn(value);
    setPassword(prev => ({ ...prev, value, score: strength.score, feedback: strength.feedback.suggestions || [], clipBoard }));
  };

  const generatePassword = () => {
    const newPassword = generator.generate({ length: 16, numbers: true });
    copyToClipboard(newPassword);
    setPasswordValue(newPassword, true);
  };

  const emailInputControl = () => {
    return (
      <FormControl fullWidth required error={emailError} disabled={props.type === 'edit'}>
        <InputLabel>User Name</InputLabel>
        <Input
          value={email}
          onChange={e => {
            const validate = e.target.value ? (!validator.isEmail(e.target.value)) : false;
            setEmailError(validate);
            setEmail(e.target.value);
          }}
        />
        <FormHelperText>{emailError ? 'A valid email id is required' : ''}</FormHelperText>
      </FormControl>
    )
  };

  const roleTextField = () => {
    if (props.showRole) {
      return (
        <TextField fullWidth label='Role' value={role}
          onChange={e => setRole(e.target.value)}
          select required
        >
          {
            ['admin', 'viewer'].map((item, idx) => {
              return (
                <MenuItem key={idx} value={item}>{item}</MenuItem>
              );
            })
          }
        </TextField>
      )
    } else {
      return null;
    }
  };

  const passwordInput = () => {
    if (showChangePassword) {
      return (
        <Button color='primary' onClick={() => setShowChangePassword(false)}>Change Password</Button>
      );
    } else {
      return (
        <>
          <FormControl fullWidth required>
            <InputLabel>Password</InputLabel>
            <Input
              type={showPassword ? 'text' : 'password'}
              value={password.value}
              onChange={e => {
                setPasswordValue(e.target.value);
              }}
              endAdornment={
                <InputAdornment position='end'>
                  <IconButton style={{ padding: 0 }} onClick={() => setShowPassword(prev => !prev)}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
          {password.clipBoard ? <div className="green fadeIn margin-top-small">Copied to clipboard</div> : null}
          <Grid container alignItems='center'>
            <Grid item xs={6} style={{ display: 'flex', alignItems: 'center' }}>
              Strength: <meter style ={{ marginLeft: 16}} max={4} min={0} value={password.score} high={3.9} optimum={4} low={2.5} />
              {password.score > 3 ? <CheckIcon className="fadeIn" style={{ color: '#009E73', height: '18px' }} /> : null}
            </Grid>
            <Grid item xs={6}>
              <Grid container justify='flex-end'>
                <Button style={{backgroundColor: 'unset', color: 'grey'}} onClick={generatePassword}>Generate</Button>
                {props.type === 'edit'
                  ? <Button onClick={() => {
                    setPassword({value: '', score: 0, feedback: [], clipBoard: false});
                    setShowChangePassword(true);
                  }}>Cancel</Button>
                  : null}
              </Grid>
            </Grid>
          </Grid>
          {
            password.feedback.map((msg, idx) => <p key={idx}>{msg}</p>)
          }
        </>
      );
    }

  }

  const contentByType = () => {
    if (props.type === 'create' || props.type === 'edit') {
      return (
        <>
          {props.currentUser
            ? <Typography style={{ color: 'red' }}>This session will expire when made changes to current user</Typography>
            : null
          }
          {emailInputControl()}
          {roleTextField()}
          {passwordInput()}
        </>
      );
    }
  };
  return (
    <Dialog
      open={props.open}
      maxWidth='xs'
      fullWidth
    >
      <DialogTitle>{titleByType()}</DialogTitle>
      <DialogContent>
        {contentByType()}
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={props.handleCancel}>Cancel</Button>
        {actionByType()}
      </DialogActions>
    </Dialog>
  );
};

UserForm.propTypes = {
  open: PropTypes.bool.isRequired,
  type: PropTypes.string,
  handleOk: PropTypes.func,
  handleCancel: PropTypes.func,
  email: PropTypes.string,
  role: PropTypes.string,
  auth0: PropTypes.bool
};

export default UserForm;
